import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import api, { mediaUrl } from "../../api/axios";

const statusLabel = { draft: "Borrador", review: "En revision", published: "Publicado", archived: "Archivado" };
const statusVariant = { draft: "secondary", review: "warning", published: "success", archived: "dark" };
const auditIssueLabels = {
  incorrect_name: "Nombre incorrecto",
  incorrect_set: "Set incorrecto",
  incorrect_number: "Numero incorrecto",
  incorrect_rarity: "Rareza incorrecta",
  incorrect_stage: "Fase o tipo incorrecto",
  incorrect_energy_type: "Tipo elemental incorrecto",
  photo_mismatch: "Imagen del catalogo incorrecta",
  other: "Otro problema"
};
const formatBytes = value => { const bytes = Number(value || 0); if (bytes < 1024) return bytes + " B"; if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"; return (bytes / (1024 * 1024)).toFixed(2) + " MB"; };
const providerImage = value => value ? (value.endsWith(".webp") ? value : value + ".webp") : "";
const catalogType = catalog => catalog.catalogType || catalog.metadata?.catalogType ||
  (catalog.metadata?.series?.id === "tcgp" || [catalog.metadata?.logoUrl, catalog.metadata?.symbolUrl].some(url => /\/tcgp(?:\/|$)/i.test(String(url || ""))) ? "pocket" : "physical");
const catalogTypeLabel = type => type === "pocket" ? "Pokémon TCG Pocket" : "Pokémon TCG físico";
const catalogDuplicateKey = catalog => [
  catalog.game_slug || catalog.game_name || "",
  catalogType(catalog),
  catalog.language || "",
  String(catalog.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "")
].join(":");

function CatalogManager() {
  const [catalogs, setCatalogs] = useState([]);
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState("");
  const [language, setLanguage] = useState("es");
  const [providerType, setProviderType] = useState("all");
  const [catalogLanguageFilter, setCatalogLanguageFilter] = useState("all");
  const [catalogTypeFilter, setCatalogTypeFilter] = useState("all");
  const [catalogStatusFilter, setCatalogStatusFilter] = useState("all");
  const [catalogDuplicateFilter, setCatalogDuplicateFilter] = useState("all");
  const [selectedCatalogIds, setSelectedCatalogIds] = useState([]);
  const [catalogPage, setCatalogPage] = useState(1);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [cardBrowser, setCardBrowser] = useState(null);
  const [cardSearch, setCardSearch] = useState("");
  const [selectedCatalogCard, setSelectedCatalogCard] = useState(null);
  const [auditContributions, setAuditContributions] = useState([]);
  const [reviewerNotes, setReviewerNotes] = useState({});
  const [auditSearch, setAuditSearch] = useState("");
  const [auditSet, setAuditSet] = useState("all");
  const [auditStatus, setAuditStatus] = useState("all");
  const [auditPage, setAuditPage] = useState(1);

  const loadCatalogs = useCallback(async () => {
    try { const { data } = await api.get("/admin/card-catalog/sets"); setCatalogs(data || []); }
    catch (requestError) { setError(requestError?.response?.data?.error || "No se pudieron cargar los catalogos."); }
  }, []);

  const loadAuditContributions = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/card-catalog/audit-contributions", { params: { limit: 200 } });
      setAuditContributions(data || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudieron cargar las fotos aportadas.");
    }
  }, []);

  useEffect(() => { loadCatalogs(); loadAuditContributions(); }, [loadCatalogs, loadAuditContributions]);
  const duplicateCounts = useMemo(() => {
    const counts = new Map();
    catalogs.filter(catalog => catalog.catalog_status !== "archived").forEach(catalog => {
      const key = catalogDuplicateKey(catalog);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [catalogs]);
  const visibleCatalogs = useMemo(() => catalogs.filter(catalog =>
    (catalogLanguageFilter === "all" || catalog.language === catalogLanguageFilter) &&
    (catalogTypeFilter === "all" || catalogType(catalog) === catalogTypeFilter) &&
    (catalogStatusFilter === "all" || catalog.catalog_status === catalogStatusFilter) &&
    (catalogDuplicateFilter === "all" || (duplicateCounts.get(catalogDuplicateKey(catalog)) > 1) === (catalogDuplicateFilter === "duplicates"))
  ), [catalogs, catalogLanguageFilter, catalogTypeFilter, catalogStatusFilter, catalogDuplicateFilter, duplicateCounts]);
  const catalogPageSize = 20;
  const catalogPageCount = Math.max(1, Math.ceil(visibleCatalogs.length / catalogPageSize));
  const paginatedCatalogs = visibleCatalogs.slice((catalogPage - 1) * catalogPageSize, catalogPage * catalogPageSize);
  useEffect(() => { setCatalogPage(1); }, [catalogLanguageFilter, catalogTypeFilter, catalogStatusFilter, catalogDuplicateFilter]);
  useEffect(() => { if (catalogPage > catalogPageCount) setCatalogPage(catalogPageCount); }, [catalogPage, catalogPageCount]);
  const auditSets = useMemo(() => [...new Map(auditContributions.filter(item => item.set_name).map(item => [item.set_name, { name: item.set_name, code: item.set_code }])).values()].sort((a, b) => a.name.localeCompare(b.name, "es")), [auditContributions]);
  const filteredContributions = useMemo(() => {
    const clean = auditSearch.trim().toLowerCase();
    return auditContributions.filter(item => {
      const matchesSearch = !clean || [item.card_name, item.collector_number, item.owner_name, item.owner_handle].some(value => String(value || "").toLowerCase().includes(clean));
      const matchesSet = auditSet === "all" || item.set_name === auditSet;
      const matchesStatus = auditStatus === "all" || (auditStatus === "without_report" ? !item.report_id : item.report_status === auditStatus);
      return matchesSearch && matchesSet && matchesStatus;
    });
  }, [auditContributions, auditSearch, auditSet, auditStatus]);
  const auditPageSize = 8;
  const auditPages = Math.max(1, Math.ceil(filteredContributions.length / auditPageSize));
  const visibleContributions = filteredContributions.slice((auditPage - 1) * auditPageSize, auditPage * auditPageSize);
  useEffect(() => { setAuditPage(1); }, [auditSearch, auditSet, auditStatus]);
  useEffect(() => { if (auditPage > auditPages) setAuditPage(auditPages); }, [auditPage, auditPages]);

  const reviewReport = async (reportId, status) => {
    setBusy("review-" + reportId); setError(""); setNotice("");
    try {
      await api.patch(`/admin/card-catalog/reports/${reportId}`, {
        status,
        reviewerNote: reviewerNotes[reportId] || ""
      });
      setNotice(status === "resolved" ? "Incidencia marcada para correccion." : "Incidencia descartada.");
      await loadAuditContributions();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudo revisar la incidencia.");
    } finally {
      setBusy("");
    }
  };

  const search = async event => {
    event?.preventDefault(); setBusy("sync-all"); setError(""); setNotice(""); setPreview(null);
    try {
      const params = { q: query, language, type: providerType };
      const { data: searchData } = await api.get("/admin/card-catalog/provider/sets", { params });
      const found = searchData.sets || [];
      setResults(found);
      if (!found.length) {
        setNotice("No se encontraron sets con esos filtros.");
        return;
      }
      const totals = { synced: 0, cards: 0, withoutImages: 0, failed: 0, failureDetails: [] };
      const syncedKeys = new Set();
      let offset = 0;
      let done = false;
      while (!done) {
        const { data } = await api.post("/admin/card-catalog/provider/sync", {
          languages: language === "all" ? ["es", "en"] : [language],
          type: providerType,
          search: query,
          offset,
          batchSize: 10
        });
        for (const item of data.results || []) {
          if (item.status === "imported" || item.status === "synced") {
            totals.synced += 1;
            totals.cards += Number(item.cards) || 0;
            syncedKeys.add(`${item.language}:${item.externalId}`);
          } else if (item.status === "without_images") totals.withoutImages += 1;
          else if (item.status === "failed") { totals.failed += 1; if (totals.failureDetails.length < 5) totals.failureDetails.push(`${item.externalId}: ${item.error || "error desconocido"}`); }
        }
        offset = Number(data.nextOffset) || offset + Number(data.processed || 0);
        done = Boolean(data.done) || !data.processed;
        setNotice(`Sincronizando sets: ${Math.min(offset, data.totalCandidates || offset)}/${data.totalCandidates || found.length}. Importados: ${totals.synced}.`);
      }
      setResults(current => current.filter(set => syncedKeys.has(`${set.language}:${set.externalId}`)).map(set => ({ ...set, imported: true })));
      setNotice(`Sincronizacion terminada: ${totals.synced} sets y ${totals.cards} cartas con imagen. Omitidos sin imagen: ${totals.withoutImages}. Errores: ${totals.failed}.${totals.failureDetails.length ? ` Detalle: ${totals.failureDetails.join("; ")}` : ""}`);
      await loadCatalogs();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudieron buscar e importar los sets.");
    } finally {
      setBusy("");
    }
  };
  const openPreview = async set => {
    setBusy("preview-" + set.externalId); setError(""); setNotice("");
    try {
      const { data } = await api.get(`/admin/card-catalog/provider/sets/${set.externalId}`, { params: { language: set.language } });
      setPreview({ ...data, imported: set.imported });
      if (!data.previewCards?.length) setNotice("El set existe, pero TCGdex aun no tiene sus cartas cargadas en este idioma.");
    } catch (requestError) { setError(requestError?.response?.data?.error || "No se pudo previsualizar el set."); }
    finally { setBusy(""); }
  };

  const importSet = async () => {
    if (!preview || preview.imported) return;
    setBusy("import"); setError(""); setNotice("");
    try {
      const { data } = await api.post(`/admin/card-catalog/provider/sets/${preview.externalId}/import`, { language: preview.language });
      setNotice(`${data.cardsCreated} cartas nuevas y ${data.cardsUpdated} actualizadas. El catalogo queda en borrador.`);
      setPreview(current => current ? { ...current, imported: true } : current);
      setResults(current => current.map(set => set.externalId === preview.externalId && set.language === preview.language ? { ...set, imported: true } : set));
      await loadCatalogs();
    } catch (requestError) { setError(requestError?.response?.data?.error || "No se pudo importar el set."); }
    finally { setBusy(""); }
  };

  const toggleCatalogSelection = catalog => {
    setNotice("");
    setSelectedCatalogIds(current => {
      if (current.includes(catalog.id)) return current.filter(id => id !== catalog.id);
      if (current.length >= 10) {
        setNotice("Puedes completar hasta 10 sets juntos.");
        return current;
      }
      return [...current, catalog.id];
    });
  };

  const enrichSelectedCatalogs = async () => {
    if (!selectedCatalogIds.length) return;
    setBusy("enrich-multiple"); setError(""); setNotice("");
    let updated = 0;
    let failed = 0;
    let remaining = selectedCatalogIds.reduce((total, id) => total + Number(catalogs.find(catalog => catalog.id === id)?.pending_enrichment || 0), 0);
    try {
      do {
        const { data } = await api.post("/admin/card-catalog/provider/enrich-selected", {
          catalogIds: selectedCatalogIds,
          limit: 50
        });
        updated += Number(data.updated) || 0;
        failed += Number(data.failed) || 0;
        remaining = Number(data.remaining) || 0;
        setNotice(`Completando ${selectedCatalogIds.length} sets: ${updated} cartas actualizadas, ${remaining} pendientes.`);
        if (!data.processed || !data.updated) break;
      } while (remaining > 0);
      setNotice(remaining
        ? `Grupo procesado: ${updated} cartas completadas; quedan ${remaining}. Errores: ${failed}. Puedes reintentar.`
        : `Sincronizacion completa: ${updated} cartas actualizadas en ${selectedCatalogIds.length} sets.`);
      if (!remaining) setSelectedCatalogIds([]);
      await loadCatalogs();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || "No se pudieron completar los sets seleccionados. El progreso se conserva.");
      await loadCatalogs();
    } finally {
      setBusy("");
    }
  };

  const enrichAllCatalogs = async () => {
    setBusy("enrich-all"); setError(""); setNotice("");
    try {
      const { data } = await api.post("/admin/card-catalog/provider/enrich", { limit: 50 });
      setNotice(`Detalles completados: ${data.updated}. Quedan ${data.remaining}.`);
      await loadCatalogs();
    } catch (requestError) { setError(requestError?.response?.data?.error || "No se pudieron completar los detalles."); }
    finally { setBusy(""); }
  };

  const syncCatalog = async catalog => {
    setBusy("sync-" + catalog.id); setError(""); setNotice("");
    try {
      const { data } = await api.post(`/admin/card-catalog/provider/sets/${catalog.provider_external_id}/import`, { language: catalog.language });
      setNotice(`Sincronizado: ${data.cardsCreated} nuevas, ${data.cardsUpdated} actualizadas.`);
      await loadCatalogs();
    } catch (requestError) { setError(requestError?.response?.data?.error || "No se pudo sincronizar el set."); }
    finally { setBusy(""); }
  };

  const enrichCatalog = async catalog => {
    setBusy("enrich-" + catalog.id); setError(""); setNotice("");
    let updated = 0;
    let failed = 0;
    let remaining = Number(catalog.pending_enrichment || 0);
    try {
      do {
        const { data } = await api.post(`/admin/card-catalog/sets/${catalog.id}/enrich`, { limit: 50 });
        updated += Number(data.updated) || 0;
        failed += Number(data.failed) || 0;
        remaining = Number(data.remaining) || 0;
        setNotice(`Completando ${catalog.name}: ${updated} cartas actualizadas, ${remaining} pendientes.`);
        if (!data.processed || !data.updated) break;
      } while (remaining > 0);
      setNotice(remaining
        ? `${catalog.name}: ${updated} cartas completadas; quedan ${remaining}. Errores: ${failed}. Puedes reintentar el set.`
        : `${catalog.name} completado: ${updated} cartas actualizadas${failed ? ` con ${failed} reintentos fallidos` : ""}.`);
      await loadCatalogs();
    } catch (requestError) {
      setError(requestError?.response?.data?.error || `No se pudo completar ${catalog.name}. El progreso anterior se conserva.`);
      await loadCatalogs();
    } finally {
      setBusy("");
    }
  };
  const loadCatalogCards = async (catalog, page = 1, search = cardSearch) => {
    setBusy("cards-" + catalog.id); setError("");
    try {
      const { data } = await api.get("/admin/card-catalog/sets/" + catalog.id + "/cards", { params: { page, limit: 36, q: search.trim() } });
      setCardBrowser({ ...data, source: catalog });
    } catch (requestError) { setError(requestError?.response?.data?.error || "No se pudieron cargar las cartas importadas."); }
    finally { setBusy(""); }
  };

  const openCatalogCards = catalog => { setCardSearch(""); loadCatalogCards(catalog, 1, ""); };

  const updateStatus = async (id, status) => {
    setBusy("status-" + id); setError(""); setNotice("");
    try {
      await api.patch(`/admin/card-catalog/sets/${id}/status`, { status });
      setNotice(status === "published" ? "Catalogo publicado y listo para progreso y faltantes." : "Estado del catalogo actualizado.");
      await loadCatalogs();
    } catch (requestError) { setError(requestError?.response?.data?.error || "No se pudo actualizar el catalogo."); }
    finally { setBusy(""); }
  };

  return (
    <div className="catalog-admin">
      {notice && <Alert variant="success" className="page-feedback">{notice}</Alert>}
      {error && <Alert variant="danger" className="page-feedback">{error}</Alert>}

      <section className="catalog-local-section">
        <div className="catalog-heading">
          <div><h2>Aportes y auditoria de cartas</h2><p className="muted-copy">Fotos reales de cartas en binders por set y reportes de datos para revision humana.</p></div>
          <Button variant="outline-dark" size="sm" onClick={loadAuditContributions}><i className="bi bi-arrow-clockwise" /> Actualizar</Button>
        </div>
        <div className="catalog-audit-controls">
          <div className="catalog-audit-search"><i className="bi bi-search" /><Form.Control value={auditSearch} onChange={event => setAuditSearch(event.target.value)} placeholder="Carta, numero o usuario" /></div>
          <Form.Select value={auditSet} onChange={event => setAuditSet(event.target.value)} aria-label="Filtrar aportes por set"><option value="all">Todos los sets</option>{auditSets.map(set => <option value={set.name} key={set.name}>{set.name + (set.code ? " · " + set.code : "")}</option>)}</Form.Select>
          <Form.Select value={auditStatus} onChange={event => setAuditStatus(event.target.value)} aria-label="Filtrar aportes por estado"><option value="all">Todos los estados</option><option value="pending">Incidencias pendientes</option><option value="resolved">Confirmadas</option><option value="dismissed">Descartadas</option><option value="without_report">Sin incidencia</option></Form.Select>
        </div>
        <div className="catalog-audit-summary"><strong>{filteredContributions.length} aportes</strong><span>{auditSet === "all" ? auditSets.length + " sets" : auditSet} · pagina {auditPage}/{auditPages}</span></div>
        <Row className="g-3">
          {visibleContributions.map(contribution => (
            <Col lg={6} key={contribution.photo_id}>
              <Card className="cardora-card h-100">
                <Card.Body>
                  <div className="d-flex gap-3">
                    <div className="d-flex gap-2 flex-shrink-0">
                      <img src={mediaUrl(contribution.photo_url)} alt={`Foto aportada de ${contribution.card_name}`} style={{ width: 96, maxHeight: 144, objectFit: "contain" }} />
                      {contribution.catalog_image_url && <img src={contribution.catalog_image_url} alt={`Imagen de catalogo de ${contribution.card_name}`} style={{ width: 96, maxHeight: 144, objectFit: "contain" }} />}
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <strong>{contribution.card_name}</strong>
                      <div className="small text-muted">{contribution.set_name} · #{contribution.collector_number || "?"} · {contribution.rarity || "Rareza pendiente"}</div>
                      <div className="small mt-2">Aportada por {contribution.owner_name || "usuario"} · {contribution.owner_handle ? `@${contribution.owner_handle} · ` : ""}{new Date(contribution.contributed_at).toLocaleString("es-ES")}</div>
                      {contribution.report_id ? (
                        <div className="mt-3">
                          <Badge bg={contribution.report_status === "pending" ? "warning" : contribution.report_status === "resolved" ? "success" : "secondary"} text={contribution.report_status === "pending" ? "dark" : undefined}>
                            {contribution.report_status === "pending" ? "Pendiente" : contribution.report_status === "resolved" ? "Incidencia confirmada" : "Descartada"}
                          </Badge>
                          <div className="small fw-semibold mt-2">{auditIssueLabels[contribution.issue_type] || contribution.issue_type} · reportado por {contribution.reporter_name || "usuario"}</div>
                          <p className="small mb-2">{contribution.report_details}</p>
                          {contribution.reviewer_note && <p className="small text-muted mb-2">Revision: {contribution.reviewer_note}</p>}
                          {contribution.report_status === "pending" && <>
                            <Form.Control
                              size="sm"
                              className="mb-2"
                              maxLength={1000}
                              placeholder="Nota interna opcional"
                              value={reviewerNotes[contribution.report_id] || ""}
                              onChange={event => setReviewerNotes(current => ({ ...current, [contribution.report_id]: event.target.value }))}
                            />
                            <div className="d-flex gap-2">
                              <Button size="sm" className="btn-cardora" disabled={busy === "review-" + contribution.report_id} onClick={() => reviewReport(contribution.report_id, "resolved")}>{busy === "review-" + contribution.report_id && <Spinner size="sm" className="me-1" />}Confirmar incidencia</Button>
                              <Button size="sm" variant="outline-dark" disabled={busy === "review-" + contribution.report_id} onClick={() => reviewReport(contribution.report_id, "dismissed")}>{busy === "review-" + contribution.report_id && <Spinner size="sm" className="me-1" />}Descartar</Button>
                            </div>
                          </>}
                        </div>
                      ) : <div className="small text-muted mt-3">Sin incidencia reportada</div>}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
          {!visibleContributions.length && <Col><div className="catalog-empty">{auditContributions.length ? "No hay aportes con estos filtros." : "Todavia no hay fotos aportadas desde binders por set."}</div></Col>}
        </Row>
        {filteredContributions.length > auditPageSize && <div className="catalog-audit-pagination">
          <Button size="sm" variant="outline-dark" disabled={auditPage <= 1} onClick={() => setAuditPage(page => page - 1)}><i className="bi bi-chevron-left" /> Anterior</Button>
          <span>{(auditPage - 1) * auditPageSize + 1}-{Math.min(auditPage * auditPageSize, filteredContributions.length)} de {filteredContributions.length}</span>
          <Button size="sm" variant="outline-dark" disabled={auditPage >= auditPages} onClick={() => setAuditPage(page => page + 1)}>Siguiente <i className="bi bi-chevron-right" /></Button>
        </div>}
      </section>

      <Card className="cardora-card mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
            <div>
              <Card.Title className="mb-1">Completar datos del catálogo</Card.Title>
              <p className="muted-copy mb-0">Completa rareza, categoría, tipos, ilustrador y variantes de las cartas importadas.</p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button variant="outline-dark" disabled={Boolean(busy)} onClick={enrichAllCatalogs}>
                {busy === "enrich-all" ? <Spinner size="sm" /> : <i className="bi bi-stars" />} <span className="ms-1">Completar 50 cartas</span>
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-4">
        <Col lg={5}>
          <Card className="cardora-card">
            <Card.Body>
              <div className="catalog-heading">
                <div><Card.Title>Importar desde TCGdex</Card.Title><p className="muted-copy">Busca el set, revisa su contenido y guárdalo en Cardora.</p></div>
                <Badge bg="success">Gratis</Badge>
              </div>
              <Form onSubmit={search} className="catalog-search">
                <Form.Select value={language} onChange={event => { setLanguage(event.target.value); setResults([]); setPreview(null); }} aria-label="Idioma del catalogo">
                  <option value="all">Todos los idiomas</option>
                  <option value="es">Español</option>
                  <option value="en">Inglés</option>
                </Form.Select>
                <Form.Select value={providerType} onChange={event => { setProviderType(event.target.value); setResults([]); setPreview(null); }} aria-label="Tipo de catalogo">
                  <option value="all">Todos los tipos</option>
                  <option value="physical">Cartas físicas</option>
                  <option value="pocket">Pokémon TCG Pocket</option>
                </Form.Select>
                <Form.Control value={query} onChange={event => setQuery(event.target.value)} placeholder="Nombre o codigo; vacio muestra todos" />
                <Button type="submit" className="btn-cardora" disabled={Boolean(busy)}>
                  {busy === "sync-all" ? <><Spinner size="sm" /> Sincronizando...</> : <><i className="bi bi-cloud-download" /> Buscar e importar sets</>}
                </Button>
              </Form>
              <div className="catalog-provider-results">
                {results.map(set => (
                  <div className="catalog-result catalog-result-direct" key={`${set.language}:${set.externalId}`}><button type="button" className="catalog-result-preview" disabled={Boolean(busy)} onClick={() => openPreview(set)}>
                    <span className="catalog-symbol">{set.symbolUrl ? <img src={providerImage(set.symbolUrl)} alt="" /> : set.logoUrl ? <img src={providerImage(set.logoUrl)} alt="" /> : <i className="bi bi-collection" />}</span>
                    <span><strong>{set.name}</strong><small>{set.externalId} | {set.language.toUpperCase()} | {catalogTypeLabel(set.catalogType)} | {set.totalCards} cartas</small></span>
                    {busy === "preview-" + set.externalId
                      ? <Spinner size="sm" />
                      : set.imported ? <Badge bg="success">Sincronizado</Badge>
                        : <Badge bg="secondary">Encontrado</Badge>}
                  </button></div>
                ))}
                {!results.length && <div className="catalog-empty">Busca una expansión o deja el campo vacío para listar todos los sets del idioma.</div>}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="cardora-card catalog-preview-card">
            <Card.Body>
              {!preview && <div className="catalog-empty catalog-empty-large"><i className="bi bi-database-add" /><strong>Selecciona un set</strong><span>Aquí verás los datos antes de importarlos.</span></div>}
              {preview && <>
                <div className="catalog-preview-head">
                  <div>{preview.logoUrl && <img src={preview.logoUrl + ".webp"} alt="" className="catalog-set-logo" />}<h2>{preview.name}</h2><p>{preview.series?.name || "Pokémon"} · {preview.releaseDate || "Sin fecha"}</p></div>
                  <div className="d-flex flex-column align-items-end gap-2"><Badge bg="light" text="dark">{preview.language.toUpperCase()}</Badge><Badge bg={preview.catalogType === "pocket" ? "info" : "secondary"}>{catalogTypeLabel(preview.catalogType)}</Badge></div>
                </div>
                <div className="catalog-preview-metrics">
                  <span><strong>{preview.totalCards}</strong>Total</span><span><strong>{preview.officialCards}</strong>Oficiales</span><span><strong>{preview.previewCards?.length || 0}</strong>En muestra</span>
                </div>
                <div className="catalog-card-strip">
                  {preview.previewCards?.map(card => <div className="catalog-card-mini" key={card.externalId}>{card.imageUrl ? <img src={card.imageUrl} alt={card.name} /> : <span>{card.localId}</span>}<small>{card.name}</small></div>)}
                </div>
                <div className="catalog-preview-actions">
                  <a href={preview.sourceUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary"><i className="bi bi-box-arrow-up-right" /> Ver fuente</a>
                  <Button className="btn-cardora" onClick={importSet} disabled={busy === "import" || preview.imported || !preview.previewCards?.length}>
                    {busy === "import" ? <Spinner size="sm" /> : <i className="bi bi-cloud-download" />} {preview.imported ? "Ya importado" : "Importar como borrador"}
                  </Button>
                </div>
              </>}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <section className="catalog-local-section">
        <div className="catalog-heading"><div><h2>Catálogos de Cardora</h2><p className="muted-copy">Solo los publicados se usarán para progreso, faltantes y alertas.</p></div><Button variant="outline-dark" size="sm" onClick={loadCatalogs}><i className="bi bi-arrow-clockwise" /> Actualizar</Button></div>
        <div className="d-flex flex-wrap gap-2">
          <Form.Select className="w-auto" value={catalogLanguageFilter} onChange={event => setCatalogLanguageFilter(event.target.value)} aria-label="Filtrar catálogos por idioma">
            <option value="all">Todos los idiomas</option><option value="es">Español</option><option value="en">Inglés</option>
          </Form.Select>
          <Form.Select className="w-auto" value={catalogTypeFilter} onChange={event => setCatalogTypeFilter(event.target.value)} aria-label="Filtrar catálogos por tipo">
            <option value="all">Todos los tipos</option><option value="physical">Cartas físicas</option><option value="pocket">Pokémon TCG Pocket</option>
          </Form.Select>
          <Form.Select className="w-auto" value={catalogStatusFilter} onChange={event => setCatalogStatusFilter(event.target.value)} aria-label="Filtrar catalogos por estado">
            <option value="all">Todos los estados</option><option value="draft">Borrador</option><option value="review">En revision</option><option value="published">Publicado</option><option value="archived">Archivado</option>
          </Form.Select>
          <Form.Select className="w-auto" value={catalogDuplicateFilter} onChange={event => setCatalogDuplicateFilter(event.target.value)} aria-label="Filtrar catálogos duplicados">
            <option value="all">Todos los sets</option><option value="duplicates">Solo duplicados</option><option value="unique">Sin duplicados</option>
          </Form.Select>
        </div>
        <small className="muted-copy">Los duplicados se detectan por nombre, idioma y tipo de catálogo. Puedes archivar uno desde su estado: dejará de mostrarse a los usuarios, pero sus cartas y binders se conservan.</small>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <small className="muted-copy">Selecciona hasta 10 sets con cartas pendientes.</small>
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="outline-dark" disabled={Boolean(busy)} onClick={() => setSelectedCatalogIds([])}>Limpiar</Button>
            <Button size="sm" className="btn-cardora" disabled={!selectedCatalogIds.length || Boolean(busy)} onClick={enrichSelectedCatalogs}>
              {busy === "enrich-multiple" ? <Spinner size="sm" /> : <i className="bi bi-stars" />} <span>{busy === "enrich-multiple" ? "Completando..." : "Completar seleccionados (" + selectedCatalogIds.length + ")"}</span>
            </Button>
          </div>
        </div>
        <div className="catalog-table-wrap">
          <table className="catalog-table">
            <thead><tr><th aria-label="Seleccionar"></th><th>Set</th><th>Cobertura</th><th>Datos</th><th>Origen</th><th>Estado</th><th>Acciones</th></tr></thead>
            <tbody>
              {paginatedCatalogs.map(catalog => {
                const expected = Number(catalog.total_cards || 0), imported = Number(catalog.imported_cards || 0);
                return <tr key={catalog.id}>
                  <td><Form.Check type="checkbox" aria-label={"Seleccionar " + catalog.name} checked={selectedCatalogIds.includes(catalog.id)} disabled={Boolean(busy) || Number(catalog.pending_enrichment || 0) === 0} onChange={() => toggleCatalogSelection(catalog)} /></td>
                  <td><div className="catalog-set-identity">{catalog.metadata?.logoUrl ? <img src={providerImage(catalog.metadata.logoUrl)} alt="" /> : catalog.metadata?.symbolUrl ? <img src={providerImage(catalog.metadata.symbolUrl)} alt="" /> : <i className="bi bi-collection" />}<span><strong>{catalog.name} {duplicateCounts.get(catalogDuplicateKey(catalog)) > 1 && <Badge bg="warning" text="dark">Duplicado</Badge>}</strong><small>{catalog.code || catalog.provider_external_id} · {catalog.language?.toUpperCase()} · {catalogTypeLabel(catalogType(catalog))}</small></span></div></td>
                  <td><strong>{imported}/{expected || "?"}</strong><div className="catalog-progress"><span style={{ width: expected ? Math.min(100, imported / expected * 100) + "%" : "0%" }} /></div></td>
                  <td><strong>{formatBytes(catalog.storage_bytes)}</strong><small>{catalog.image_count || 0} URLs de imagen � {catalog.rarity_count || 0} rarezas</small></td>
                  <td><span>{catalog.provider === "community" ? "Sugerencia de comunidad" : "TCGdex"}</span><small>{catalog.last_synced_at ? new Date(catalog.last_synced_at).toLocaleString("es-ES") : "Sin sincronizar"}</small></td>
                  <td><Badge bg={statusVariant[catalog.catalog_status] || "secondary"} text={catalog.catalog_status === "review" ? "dark" : undefined}>{statusLabel[catalog.catalog_status] || catalog.catalog_status}</Badge></td>
                  <td><div className="catalog-row-actions">
                    <Button variant="outline-dark" size="sm" title="Ver cartas importadas" disabled={busy === "cards-" + catalog.id} onClick={() => openCatalogCards(catalog)}><i className="bi bi-grid-3x3-gap" /> Ver cartas</Button>
                    <Form.Select size="sm" value={catalog.catalog_status} disabled={busy === "status-" + catalog.id} onChange={event => updateStatus(catalog.id, event.target.value)}><option value="draft">Borrador</option><option value="review">En revisi�n</option><option value="published">Publicado</option><option value="archived">Archivado</option></Form.Select>
                    <Button variant="outline-dark" size="sm" title="Sincronizar listado" disabled={busy === "sync-" + catalog.id || catalog.provider !== "tcgdex" || !catalog.provider_external_id} onClick={() => syncCatalog(catalog)}><i className="bi bi-arrow-repeat" /></Button>
                    <Button variant="outline-dark" size="sm" title="Completar todas las cartas pendientes de este set" disabled={Boolean(busy) || Number(catalog.pending_enrichment || 0) === 0} onClick={() => enrichCatalog(catalog)}>{busy === "enrich-" + catalog.id ? <Spinner size="sm" /> : <i className="bi bi-stars" />} <span>{busy === "enrich-" + catalog.id ? "Completando" : "Completar (" + (catalog.pending_enrichment || 0) + ")"}</span></Button>
                  </div></td>
                </tr>;
              })}
              {!visibleCatalogs.length && <tr><td colSpan="7" className="catalog-empty">{catalogs.length ? "No hay catálogos con estos filtros." : "Todavía no hay catálogos importados."}</td></tr>}
            </tbody>
          </table>
        </div>
        {visibleCatalogs.length > 0 && <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <small className="muted-copy">Mostrando {(catalogPage - 1) * catalogPageSize + 1}-{Math.min(catalogPage * catalogPageSize, visibleCatalogs.length)} de {visibleCatalogs.length} sets</small>
          <div className="d-flex align-items-center gap-2">
            <Button size="sm" variant="outline-dark" disabled={catalogPage <= 1} onClick={() => setCatalogPage(page => page - 1)}>Anterior</Button>
            <small className="muted-copy">Página {catalogPage} de {catalogPageCount}</small>
            <Button size="sm" variant="outline-dark" disabled={catalogPage >= catalogPageCount} onClick={() => setCatalogPage(page => page + 1)}>Siguiente</Button>
          </div>
        </div>}
      </section>

      <Modal show={Boolean(cardBrowser)} onHide={() => setCardBrowser(null)} size="xl" centered scrollable contentClassName="catalog-browser-modal">
        <Modal.Header closeButton>
          <Modal.Title>{cardBrowser?.catalog?.name || "Cartas importadas"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={event => { event.preventDefault(); if (cardBrowser?.source) loadCatalogCards(cardBrowser.source, 1); }} className="catalog-browser-search">
            <Form.Control value={cardSearch} onChange={event => setCardSearch(event.target.value)} placeholder="Buscar por nombre o numero" />
            <Button type="submit" variant="outline-dark"><i className="bi bi-search" /> Buscar</Button>
          </Form>
          <div className="catalog-browser-summary"><strong>{cardBrowser?.total || 0} cartas</strong><span>Pagina {cardBrowser?.page || 1} de {cardBrowser?.pages || 1}</span></div>
          <div className="catalog-browser-grid">
            {cardBrowser?.cards?.map(card => (
              <button type="button" className="catalog-browser-card" key={card.id} onClick={() => setSelectedCatalogCard(card)}>
                <div className="catalog-browser-image">{card.image_url ? <img src={card.image_url} alt={card.name} /> : <i className="bi bi-card-image" />}</div>
                <div><strong>{card.name}</strong><span>#{card.collector_number || "?"}</span><small>{card.rarity || "Rareza pendiente"}</small></div>
                <Badge bg={card.enriched ? "success" : "secondary"}>{card.enriched ? "Completa" : "Pendiente"}</Badge>
              </button>
            ))}
          </div>
          {!cardBrowser?.cards?.length && <div className="catalog-empty">No hay cartas con ese filtro.</div>}
        </Modal.Body>
        <Modal.Footer className="catalog-browser-pagination">
          <Button variant="outline-dark" disabled={!cardBrowser || cardBrowser.page <= 1 || busy.startsWith("cards-")} onClick={() => loadCatalogCards(cardBrowser.source, cardBrowser.page - 1)}><i className="bi bi-chevron-left" /> Anterior</Button>
          <span>{cardBrowser?.page || 1} / {cardBrowser?.pages || 1}</span>
          <Button variant="outline-dark" disabled={!cardBrowser || cardBrowser.page >= cardBrowser.pages || busy.startsWith("cards-")} onClick={() => loadCatalogCards(cardBrowser.source, cardBrowser.page + 1)}>Siguiente <i className="bi bi-chevron-right" /></Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(selectedCatalogCard)} onHide={() => setSelectedCatalogCard(null)} centered contentClassName="catalog-card-detail-modal">
        <Modal.Header closeButton><Modal.Title>{selectedCatalogCard?.name || "Carta"}</Modal.Title></Modal.Header>
        <Modal.Body>{selectedCatalogCard && <div className="catalog-card-detail">
          <div className="catalog-card-detail-image">{selectedCatalogCard.image_url ? <img src={selectedCatalogCard.image_url} alt={selectedCatalogCard.name} /> : <i className="bi bi-card-image" />}</div>
          <dl><div><dt>Numero</dt><dd>{selectedCatalogCard.collector_number || "Pendiente"}</dd></div><div><dt>Rareza</dt><dd>{selectedCatalogCard.rarity || "Pendiente de enriquecer"}</dd></div><div><dt>Categoria</dt><dd>{selectedCatalogCard.metadata?.category || "Pendiente"}</dd></div><div><dt>Tipo</dt><dd>{selectedCatalogCard.metadata?.types?.join(", ") || selectedCatalogCard.metadata?.energyType || "Pendiente"}</dd></div><div><dt>Idioma</dt><dd>{selectedCatalogCard.metadata?.language?.toUpperCase() || cardBrowser?.catalog?.language?.toUpperCase() || "N/D"}</dd></div><div><dt>Origen</dt><dd>{selectedCatalogCard.metadata?.providerExternalId || selectedCatalogCard.external_id}</dd></div></dl>
        </div>}</Modal.Body>
      </Modal>
    </div>
  );
}
export default CatalogManager;
